import React from "react";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";

import { formatDateLabel } from "../../../utils/dateOnly";
import { buildCriteria, formatCepLabel, formatPesoLabel, formatRouteLabel } from "./regras";

// Detalhe de uma linha do recálculo: critérios usados para achar tabela, custo e
// taxa e os eventos do cálculo. Mesmo conteúdo de antes (sprint 638 só trocou a
// casca para o modal padrão do Hub, com tokens de tema).

function CriteriaSection({ title, description, items }) {
  if (!items.length) return null;
  return (
    <section className="recalc-log__secao">
      <div className="recalc-log__secao-titulo">{title}</div>
      {description ? <p className="recalc-log__secao-descricao">{description}</p> : null}
      <div className="recalc-log__grid">
        {items.map((item) => (
          <div key={item.label} className="recalc-log__campo">
            <div className="recalc-log__campo-rotulo">{item.label}</div>
            <div className="recalc-log__campo-valor">{item.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const TONS = {
  erro: { classe: "danger", icone: "✕", rotulo: "Erro" },
  aviso: { classe: "warning", icone: "!", rotulo: "Atenção" },
  ok: { classe: "success", icone: "✓", rotulo: "Confirmado" },
};

export default function LogModal({ row, onClose }) {
  if (!row) return null;
  const log = row._log || {};
  const contexto = log.contexto || {};
  const freteTabela = log.freteTabela;
  const custos = log.custos || [];
  const taxas = log.taxas || [];
  const rota = formatRouteLabel(contexto.origem, contexto.destino);
  const erroTexto = row.erro || "";
  const erroTabelaFrete = /tabela de frete n[aã]o encontrada/i.test(erroTexto);
  const cepDestino = formatCepLabel(contexto.cep);
  const pesoConsiderado = formatPesoLabel(contexto.peso);
  const dataEmissao = formatDateLabel(contexto.dataEmissao);

  const linhas = [];

  if (row.status === "Erro") {
    linhas.push({ tipo: "erro", texto: erroTexto || "Erro desconhecido" });
  }

  if (freteTabela) {
    linhas.push({ tipo: "ok", texto: `Tabela de frete: ${freteTabela.nome} (id: ${freteTabela.id})` });
  } else if (!erroTabelaFrete) {
    linhas.push({ tipo: "aviso", texto: "Nenhuma tabela de frete encontrada" });
  }

  if (freteTabela) {
    if (custos.length === 0) {
      linhas.push({ tipo: "aviso", texto: "Nenhuma tabela de custo encontrada" });
    } else {
      custos.forEach((c) => {
        const desc = c.DescricaoFreteCusto || c.NomeFreteCusto || JSON.stringify(c);
        const valor = c.ValorRegra != null ? ` — Valor: ${c.ValorRegra}` : "";
        linhas.push({ tipo: "ok", texto: `Custo: ${desc}${valor}` });
      });
    }

    if (taxas.length === 0) {
      linhas.push({ tipo: "aviso", texto: "Nenhuma tabela de taxa encontrada" });
    } else {
      taxas.forEach((t) => {
        const desc = t.DescricaoFreteTaxa || t.NomeFreteTaxa || t.CodigoFreteTaxa || "";
        const tipo = t.TipoFreteTaxa ? ` (${t.TipoFreteTaxa})` : "";
        const valor = t.ValorRegra != null ? ` — Valor: ${t.ValorRegra}` : "";
        linhas.push({ tipo: "ok", texto: `Taxa: ${desc}${tipo}${valor}` });
      });
    }
  }

  const criteriosFrete = buildCriteria([
    { label: "Origem", value: contexto.origem },
    { label: "Destino", value: contexto.destino },
    { label: "CEP destino", value: cepDestino },
    { label: "Data emissão", value: dataEmissao },
    { label: "Modal", value: contexto.modal },
    { label: "Negócio", value: contexto.negocio },
    { label: "Tipo de serviço", value: contexto.tipoServico },
    { label: "Serviço", value: contexto.servico },
    { label: "Tipo de frete", value: contexto.tipoFrete },
    { label: "Unidade de frete", value: contexto.unidadeFrete },
    { label: "Tipo de veículo", value: contexto.tipoVeiculo },
  ]);
  const criteriosCusto = buildCriteria([
    { label: "Tabela de frete", value: freteTabela ? `${freteTabela.nome} (id: ${freteTabela.id})` : null },
    { label: "Destino", value: contexto.destino },
    { label: "CEP destino", value: cepDestino },
    { label: "Peso considerado", value: pesoConsiderado },
  ]);
  const criteriosTaxa = buildCriteria([
    { label: "Tabela de frete", value: freteTabela ? `${freteTabela.nome} (id: ${freteTabela.id})` : null },
    { label: "Destino", value: contexto.destino },
    { label: "CEP destino", value: cepDestino },
  ]);

  return (
    <Modal isOpen toggle={onClose} size="lg" modalClassName="modal-align-top" fade={false} className="recalc-log">
      <ModalHeader toggle={onClose} tag="div">
        <div className="recalc-log__eyebrow">Auditoria do recálculo</div>
        <h5 className="recalc-log__titulo">Detalhes do cálculo</h5>
        <p className="recalc-log__subtitulo">
          Entenda quais critérios foram usados para localizar tabelas, custos e taxas desta execução.
        </p>
      </ModalHeader>
      <ModalBody className="recalc-log__corpo">
        <div className="recalc-log__resumo">
          <div className="recalc-log__campo">
            <div className="recalc-log__campo-rotulo">Notas fiscais</div>
            <div className="recalc-log__campo-valor">{row.numerosNFs || "-"}</div>
          </div>
          <div className="recalc-log__campo">
            <div className="recalc-log__campo-rotulo">Transportadora</div>
            <div className="recalc-log__campo-valor">{row.transportadora || "-"}</div>
          </div>
          {rota ? (
            <div className="recalc-log__campo">
              <div className="recalc-log__campo-rotulo">Rota considerada</div>
              <div className="recalc-log__campo-valor">{rota}</div>
            </div>
          ) : null}
        </div>
        <CriteriaSection
          title="Busca da tabela de frete"
          description="Critérios base usados para localizar a tabela principal de cálculo."
          items={criteriosFrete}
        />
        {freteTabela ? (
          <CriteriaSection
            title="Busca da tabela de custo"
            description="Recorte adicional aplicado para localizar custos complementares."
            items={criteriosCusto}
          />
        ) : null}
        {freteTabela ? (
          <CriteriaSection
            title="Busca da tabela de taxa"
            description="Parâmetros usados para encontrar regras de taxa associadas ao frete."
            items={criteriosTaxa}
          />
        ) : null}
        <section className="recalc-log__secao">
          <div className="recalc-log__secao-titulo">Eventos do cálculo</div>
          <p className="recalc-log__secao-descricao">
            Registro consolidado da busca por tabela, custos, taxas e mensagens de erro da execução.
          </p>
          <div className="recalc-log__eventos" data-testid="recalc-log-eventos">
            {linhas.map((linha, index) => {
              const tom = TONS[linha.tipo];
              return (
                <div key={`${linha.texto}-${index}`} className={`recalc-evento recalc-evento--${tom.classe}`}>
                  <div className="recalc-evento__rotulo">
                    {tom.icone} {tom.rotulo}
                  </div>
                  <div className="recalc-evento__texto">{linha.texto}</div>
                </div>
              );
            })}
          </div>
        </section>
        <div className="recalc-modal-rodape">
          <Button type="button" color="default" outline size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
